import { blue, grey } from '@mui/material/colors';
import { alpha } from '@mui/material/styles';
import {
	$danger,
	$warning,
	$success,
	$dangerLight,
	$dangerDark,
	$warningLight,
	$warningDark,
	$successLight,
	$successDark,
	$white,
	$black,
	$defaultDarkPaper // eslint-disable-line no-unused-vars
} from 'constants/styles/_colors';
import GraphikRegFont from 'assets/fonts/Graphik-Regular-Web.woff2';

// Function to get design tokens based on mode (following MUI v5 dark mode docs pattern)
export const getTheme = mode => ({
	palette: {
		mode,
		primary: blue,
		secondary: grey,
		error: {
			main: $danger,
			light: $dangerLight,
			dark: $dangerDark,
			contrastText: mode === 'dark' ? $white : $white
		},
		warning: {
			main: $warning,
			light: $warningLight,
			dark: $warningDark,
			contrastText: $black
		},
		success: {
			main: $success,
			light: $successLight,
			dark: $successDark,
			contrastText: $black
		},
		...(mode === 'light'
			? {
					// Light mode
					background: {
						default: $white,
						background1: grey[200], // darker
						background2: grey[300] // darker still
					},
					text: {
						primary: $black,
						secondary: grey[700]
					}
				}
			: {
					// Dark mode
					background: {
						default: '#121212',
						background1: grey[900], // lighter
						background2: grey[800] // lighter still,
					},
					text: {
						primary: $white,
						secondary: grey[400]
					}
				})
	},
	typography: {
		useNextVariants: true,
		suppressDeprecationWarnings: true,
		fontFamily: [
			'Graphik',
			'-apple-system',
			'BlinkMacSystemFont',
			'"Segoe UI"',
			'Roboto',
			'"Helvetica Neue"',
			'Arial',
			'sans-serif',
			'"Apple Color Emoji"',
			'"Segoe UI Emoji"',
			'"Segoe UI Symbol"'
		].join(',')
	},
	components: {
		MuiAppBar: {
			defaultProps: {
				// In dark mode, MUI AppBar defaults to a dark background unless this is enabled.
				// We want AppBars (e.g., Mission form + Files gallery) to stay `color="primary"` in dark mode too.
				enableColorOnDark: true
			}
		},
		// Standardize tab *bars* (Tabs/TabList) to a neutral grey surface.
		// Keep vertical tabs (MainNav) transparent so the nav rail controls its own background.
		MuiTabs: {
			styleOverrides: {
				root: ({ theme, ownerState }) => ({
					backgroundColor:
						ownerState?.orientation === 'vertical'
							? 'transparent'
							: theme.palette.background.background1
				})
			}
		},
		// Improve Alert contrast in dark mode (standard/outlined alerts can disappear on a dark canvas).
		MuiAlert: {
			styleOverrides: {
				root: ({ theme, ownerState }) => {
					const isDark = theme.palette.mode === 'dark';
					const variant = ownerState?.variant;
					const severity = ownerState?.severity || 'info';

					const severityPalette = theme.palette?.[severity] || theme.palette.info;
					const severityMain = severityPalette?.main || theme.palette.info.main;

					// Make alerts "pop" in dark mode by tinting the surface with the severity color
					// and adding a colored left border.
					const darkTint =
						variant === 'outlined' ? alpha(severityMain, 0.12) : alpha(severityMain, 0.18);

					return {
						backgroundImage: 'none',
						...(isDark &&
							(variant === 'standard' || variant === 'outlined') && {
								backgroundColor: darkTint,
								color: theme.palette.text.primary,
								border: `1px solid ${alpha(severityMain, 0.45)}`,
								borderLeft: `4px solid ${severityMain}`
							})
					};
				},
				icon: ({ theme, ownerState }) => {
					const severity = ownerState?.severity || 'info';
					const severityPalette = theme.palette?.[severity] || theme.palette.info;
					const severityMain = severityPalette?.main || theme.palette.info.main;

					return {
						color: theme.palette.mode === 'dark' ? severityMain : undefined
					};
				}
			}
		},
		MuiDrawer: {
			styleOverrides: {
				paper: ({ theme }) => ({
					backgroundColor: theme.palette.background.default,
					color: theme.palette.text.primary,
					// Remove MUI dark-mode elevation overlay so the drawer matches `background.default` exactly.
					backgroundImage: 'none'
				})
			}
		},
		MuiCssBaseline: {
			styleOverrides: `
        @font-face {
          font-family: 'Graphik';
          font-size: 0.875rem;
          font-style: normal;
          font-display: swap;
          src: local('Graphik'), local('Graphik-Regular-Web'), url(${GraphikRegFont}) format('woff2');
          unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
        }
      `
		},
		MuiLink: {
			defaultProps: {
				underline: 'hover',
				color: 'primary'
			},
			styleOverrides: {
				root: ({ theme }) => ({
					color: theme.palette.primary.main,
					'&:visited': {
						color: theme.palette.primary.main
					},
					'&:hover': {
						color: theme.palette.primary.light
					}
				})
			}
		},
		MuiTabPanel: {
			styleOverrides: {
				root: {
					padding: '0px'
				}
			}
		}
	}
});
